import clsx from "clsx";
import { FormEvent, forwardRef, useEffect, useState } from "react";

interface Props {
  className?: string,
  inputClassName?: string,
  type: "text" | "email" | "password",
  name: string,
  value?: string,
  defaultValue?: string,
  pattern?: string,
  placeholder?: string,
  isInvalid?: boolean,
  onChange?: (value: string) => void,
  ariaLabel?: string;
  autoFocus?: boolean;
}

const TextInput = forwardRef<HTMLInputElement, Props>((
    {
      className = null,
      inputClassName = null,
      type,
      name,
      value = null,
      defaultValue = null,
      pattern = undefined,
      placeholder = "",
      isInvalid = false,
      onChange = () => {},
      ariaLabel = undefined,
      autoFocus = undefined
    }, ref
  ) => {
    const [state, setState] = useState<string>(value ?? (defaultValue ?? ""));

    useEffect(() => {
      onChange(state);
    }, [state]);

    useEffect(() => {
      setState(String(value));
    }, [value]);

    if (name.length === 0) {
      throw new Error("Empty input name");
    }

    /**
     * Prevent input of invalid characters
     */
    const handleInput = (e: FormEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      const inputValue = input.value;

      if (pattern) {
        console.log("Filtering input", pattern);

        const regex = new RegExp(pattern);

        // If the input value matches the pattern (or is empty), update the state
        if (regex.test(inputValue) || inputValue === "") {
          setState(inputValue);
        } else {
          // Prevent the invalid character(s) from being added
          input.value = state;
        }
      } else {
        setState(inputValue);
      }
    };

    let icon;
    switch (type) {
      case "password":
        icon = (
          <span className="absolute">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         className="w-6 h-6 mx-3 text-primary-300 dark:text-primary-500"
                         fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </span>
        );
        break;
      case "email":
        icon = (
          <span className="absolute">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         className="w-6 h-6 mx-3 text-primary-300 dark:text-primary-500"
                         fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                </span>
        );
        break;
      default:
        icon = null;
    }

    return (
      <div className={clsx(
        "relative flex items-center w-full",
        className
      )}>
        {icon}

        <input type={type}
               value={state}
               id={name}
               name={name}
               className={clsx(
                 "block",
                 {
                   "px-11": icon !== null,
                   "px-3": icon === null,
                   "invalid": isInvalid
                 },
                 inputClassName
               )}
               placeholder={placeholder}
               onInput={handleInput}
               autoFocus={autoFocus}
               {...(ref ? { ref } : {})}
               aria-label={ariaLabel}
               {...(pattern ? { pattern } : {})}
        />
      </div>
    );
  }
);
export default TextInput;